import { ClipboardCheck, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexAlertTiles } from '../../../../components/index-page/IndexAlertTiles';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { maintenanceCategoriesApi, maintenanceItemsApi } from '../buildingManagementApi';
import {
  AGING_FACET,
  BUILDING_MANAGEMENT_AUDIT_TYPES,
  MAINTENANCE_AGING,
  type MaintenanceItemDetail,
  type MaintenanceItemListItem,
} from '../buildingManagementTypes';
import { AgingStatus } from '../components/AgingStatus';
import { AGING_DOT } from '../components/maintenanceCalendarDots';
import { MaintenanceItemModal } from '../components/MaintenanceItemModal';
import { MaintenanceYearCalendar } from '../components/MaintenanceYearCalendar';
import { RecordMaintenanceModal } from '../components/RecordMaintenanceModal';
import { maintenanceItemTitle } from '../utils/maintenanceItemTitle';

/**
 * Every aging band as a card, each with its calendar DOT — the cards are the page's colour
 * legend as well as its monitor (product call, 12 Oct 2026).
 */
const MONITORED = [
  { value: AGING_FACET.onSchedule, aging: MAINTENANCE_AGING.onSchedule },
  { value: AGING_FACET.nearExpiration, aging: MAINTENANCE_AGING.nearExpiration },
  { value: AGING_FACET.expired, aging: MAINTENANCE_AGING.expired },
  { value: AGING_FACET.expiredBeyondThreshold, aging: MAINTENANCE_AGING.expiredBeyondThreshold },
];

/**
 * Building Management — every building-related maintenance item, monitored by aging.
 *
 * **The page's first job is the four aging cards**: On Schedule, Near Expiration, Expired, and the
 * escalated band ("30 Days Expired", where 30 is the setting), each with its calendar dot, so
 * they are the colour legend too. Each is the server's count on the `aging` facet
 * and narrows the list in one press — the product owner's monitoring requirement, in their own
 * words. The register under them is soonest-due first, so the most overdue item leads.
 *
 * **Three views, and CALENDAR is the default** (product call, 12 Oct 2026) — the year as twelve
 * month columns of due dates, through the shell's `extraView`. Table and List stay one click away
 * in the cog, and a reader's own choice persists.
 *
 * **No Clear data** — emptying a register of permits and inspections is not the tidy-up that key
 * exists for, and the module declares none.
 */
export function MaintenanceIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);

  /*
    Edit and Record both need the DETAIL — the responsible employee, the contractor, the server's
    suggested next due date — which the list row deliberately does not carry. So the row action
    fetches it first and the modal opens over the answer.
  */
  const [editing, setEditing] = useState<MaintenanceItemDetail | null>(null);
  const [recording, setRecording] = useState<MaintenanceItemDetail | null>(null);

  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    row: MaintenanceItemListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    THE ONE REQUIRED CATALOG — an item cannot be registered without a category. Seeded MET, so a
    feed that has not answered (or failed, or that this reader cannot call) never puts a banner
    over a working module.
  */
  const [hasCategory, setHasCategory] = useState(true);

  /*
    THE BUILDING, required since 12 Oct 2026 and kept in Logistics' real-property register — so a
    fresh database needs a property registered THERE before an item can be. Asked through this
    module's own building feed, which answers only a reader who can write an item; anyone else
    never meets the form, and the entry stays seeded MET.
  */
  const canWriteItem = usePermission('building-management.create');
  const [hasBuilding, setHasBuilding] = useState(true);

  useEffect(() => {
    if (!canWriteItem) return;

    const controller = new AbortController();

    maintenanceItemsApi
      .buildings(controller.signal)
      .then((options) => {
        if (!controller.signal.aborted) setHasBuilding(options.length > 0);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [canWriteItem, reloadKey]);

  useEffect(() => {
    const controller = new AbortController();

    maintenanceCategoriesApi
      .options(controller.signal)
      .then((options) => {
        if (!controller.signal.aborted) setHasCategory(options.length > 0);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [reloadKey]);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      maintenanceItemsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          aging: query.facets.aging?.join(','),
          category: query.facets.category?.join(','),
          building: query.facets.building?.join(','),
          recurrence: query.facets.recurrence?.join(','),
        },
        signal,
      ),
    [],
  );

  const openWithDetail = useCallback(
    (row: MaintenanceItemListItem, then: (detail: MaintenanceItemDetail) => void) => {
      maintenanceItemsApi
        .get(row.id)
        .then(then)
        // No form is open yet to report into, so the failure goes to a toast.
        .catch((caught: unknown) =>
          toast.failure(caught instanceof ApiError ? caught.message : `Could not open ${row.referenceNumber}.`),
        );
    },
    [toast],
  );

  const rowActions = useCallback(
    (row: MaintenanceItemListItem): MenuItem[] => [
      {
        key: `record-${row.id}`,
        label: 'Record activity',
        icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
        permission: 'building-management.record',
        disabledReason: row.isActive ? null : 'This item is deleted. Restore it before recording against it.',
        onClick: () => openWithDetail(row, setRecording),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'building-management.delete',
        disabledReason: row.isActive ? 'This item is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [openWithDetail],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await maintenanceItemsApi.remove(confirming.row.id);
      else await maintenanceItemsApi.restore(confirming.row.id);

      toast.success(
        confirming.kind === 'delete'
          ? `Deleted ${confirming.row.referenceNumber}.`
          : `Restored ${confirming.row.referenceNumber}.`,
      );
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<MaintenanceItemListItem>
        module="building-management"
        moduleNoun="maintenance items"
        breadcrumb={['General Services', 'Building Management', 'Maintenance']}
        columns={COLUMNS}
        facets={[
          { key: 'aging', label: 'Aging' },
          { key: 'category', label: 'Category' },
          { key: 'building', label: 'Building' },
          { key: 'recurrence', label: 'Recurrence' },
          { key: 'status', label: 'Status' },
        ]}
        auditEntityTypes={BUILDING_MANAGEMENT_AUDIT_TYPES}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by reference, category, building or location"
        emptyMessage="No maintenance items yet. Register the permits, servicing and inspections the buildings carry."
        aboveTable={({ counts, selected, selectFacet, loading }) => (
          <IndexAlertTiles
            tiles={MONITORED.map(({ value, aging }) => {
              const facet = counts.aging?.find((f) => f.value === value);

              return { value, label: facet?.label ?? '—', count: facet ? facet.count : null, marker: AGING_DOT[aging] };
            })}
            selected={selected.aging ?? []}
            loading={loading}
            onSelect={(values) => selectFacet('aging', values)}
            emptyReason={(tile) => `Nothing in this list is ${tile.label}.`}
          />
        )}
        defaultView="extra"
        extraView={{
          label: 'Calendar',
          render: (query) => (
            <MaintenanceYearCalendar
              filters={{
                search: query.search,
                status: query.facets.status?.join(','),
                aging: query.facets.aging?.join(','),
                category: query.facets.category?.join(','),
                building: query.facets.building?.join(','),
                recurrence: query.facets.recurrence?.join(','),
              }}
              reloadKey={reloadKey}
            />
          ),
        }}
        prerequisites={[
          {
            // In Logistics, not here — the link is offered only to a reader who can open that page.
            label: 'real properties',
            met: hasBuilding,
            to: '/inventory/settings/real-properties',
            permission: 'real-properties.read',
          },
          {
            // PLURAL, for the shell's three sentences — the call `categories` takes elsewhere.
            label: 'maintenance categories',
            met: hasCategory,
            to: '/general-services/building-management/settings/categories',
            permission: 'maintenance-categories.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="building-management.create"
        onRowOpen={(row) => navigate(`/general-services/building-management/maintenance/${row.id}`)}
        openPermission="building-management.read"
        rowActions={rowActions}
        onEdit={(row) => openWithDetail(row, setEditing)}
        editPermission="building-management.update"
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="building-management.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This item is already deleted.')}
        otherInfo={(row) => row}
      />

      {creating || editing !== null ? (
        <MaintenanceItemModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {recording ? (
        <RecordMaintenanceModal
          open
          item={recording}
          onClose={() => setRecording(null)}
          onSaved={() => {
            setRecording(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete maintenance item' : 'Restore maintenance item'}
          danger={confirming.kind === 'delete'}
          // Both safeguards on the destructive half only. A restore puts something back.
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`maintenance-item-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <p>
                  {confirming.row.referenceNumber} — {maintenanceItemTitle(confirming.row)} leaves the register, the
                  calendar and the aging counts. Its activities are kept, and a deleted item
                  can be restored from this list.
                </p>
              ) : (
                <p>
                  {confirming.row.referenceNumber} — {maintenanceItemTitle(confirming.row)} returns to the register and
                  is aged against its due date again.
                </p>
              )}

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<MaintenanceItemListItem>[] = [
  {
    /*
      THE ANCHOR joins the reference and the item's TITLE on ONE line — the anchor exception, one
      header, one width. An item has no name (12 Oct 2026): it is its category in its building,
      narrowed by its location, so those three are the title and have no columns of their own —
      a column beside it would print the same value twice. Their facets stay in the rail, and the
      sort runs by category, then building.
    */
    key: 'categoryName',
    header: 'Item',
    width: 360,
    flex: true,
    sortable: true,
    render: (row) => <TextCell code={row.referenceNumber} value={maintenanceItemTitle(row)} clamp />,
  },
  {
    key: 'recurrence',
    header: 'Recurrence',
    width: 120,
    summary: true,
    render: (row) => <TextCell value={row.recurrenceLabel} />,
  },
  {
    // A CALENDAR date, so `formatDateOnly` and never `<DateCell>`, which is for an instant.
    key: 'dueDate',
    header: 'Due Date',
    width: 130,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={formatDateOnly(row.dueDate)} mono />,
  },
  {
    /*
      SIGNED, from the server's own "today", so it cannot disagree with the band beside it.
      Not sortable as its own field: it orders exactly as the due date does.
    */
    key: 'daysUntilDue',
    header: 'Days Until Due',
    width: 130,
    align: 'right',
    render: (row) => <TextCell value={String(row.daysUntilDue)} mono numeric />,
  },
  {
    key: 'lastPerformedOn',
    header: 'Last Performed',
    width: 140,
    // A correlated MAX — not sortable, or the database computes one per row before paging.
    render: (row) => (
      <TextCell value={row.lastPerformedOn != null ? formatDateOnly(row.lastPerformedOn) : null} mono />
    ),
  },
  {
    key: 'aging',
    header: 'Aging',
    width: 160,
    status: true,
    render: (row) => <AgingStatus aging={row.aging} label={row.agingLabel} isActive={row.isActive} />,
  },
];
